import React from 'react';
import styled from 'styled-components/macro';
import { TablePaginationProps } from './ReactTablePagination.types';
import { Button, Icon } from '../../UI';
import { Select } from '../../Forms';

const ReactTablePagination: React.FC<TablePaginationProps> = (props) => {
	const pageRef = React.useRef<number>(0);

	const { page, pageIndex, pageCount, pageOptions, totalResults, canPreviousPage, canNextPage, gotoPage, previousPage, nextPage } = props;

	/**
	 * Set pageState to the value from the input field
	 *
	 * @param {string} value
	 * @returns {void}
	 */
	const pageChangedHandler = React.useCallback((value: string) => {
		pageRef.current = value ? Number(value) - 1 : 0;
	}, []);

	/**
	 * Go to the page specified in the input
	 *
	 *  @returns {void}
	 */
	const pageSubmitHandler = React.useCallback(() => {
		gotoPage(pageRef.current);
	}, [gotoPage]);

	return (
		<ScContainer>
			<ScShowing>
				{!props.paginationSelectOptions && `Visar ${page.length} av ${totalResults} resultat`}

				{props.paginationSelectOptions && props.resultsPerPageChanged && (
					<ScSelectWrapper>
						Visar
						<ScSelect
							hasNoMinWidth
							value={props.resultsPerPage}
							changed={props.resultsPerPageChanged}
						>
							{props.paginationSelectOptions.map(option => (
								<option
									key={option}
									value={option}
								>
									{option}
								</option>
							))}
						</ScSelect>
						{`av ${totalResults} resultat`}
					</ScSelectWrapper>
				)}
			</ScShowing>
		
			<ScTablePagination>
				<ScPrev
					onClick={() => gotoPage(0)}
					disabled={!canPreviousPage}
				>
					<ScIcon icon={['fal', 'angles-left']} />
				</ScPrev>
				{' '}
				<ScPrev
					onClick={() => previousPage()}
					disabled={!canPreviousPage}
				>
					<ScIcon icon={['fal', 'angle-left']} />
				</ScPrev>
				<ScPages>
					Sida
					{' '}
					<strong>
						{`${pageIndex + 1} av ${pageOptions.length}`}
					</strong>
				</ScPages>
				<ScGoTo>
					Gå till sida:
					<ScGoToInput
						type="number"
						defaultValue={pageIndex + 1}
						onChange={(ev) => {
							pageChangedHandler(ev.target.value);
						}}
					/>
					<ScButton
						isXSmall
						isPrimary
						isRounded
						onClick={pageSubmitHandler}
					>
						Gå
					</ScButton>
				</ScGoTo>
				<ScNext
					onClick={() => nextPage()}
					disabled={!canNextPage}
				>
					<ScIcon icon={['fal', 'angle-right']} />
				</ScNext>
				<ScNext
					onClick={() => gotoPage(pageCount - 1)}
					disabled={!canNextPage}
				>
					<ScIcon icon={['fal', 'angles-right']} />
				</ScNext>
			</ScTablePagination>
		</ScContainer>
	);
};

export default React.memo(ReactTablePagination);

const ScContainer = styled.div`
	display: flex;
	margin-top: 8px;
	justify-content: space-between;
`;

const ScShowing = styled.div`
	font-size: 14px;
`;

const ScTablePagination = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
`;

const ScNextandPrev = styled.button`
	display: flex;
	appearance: none;
	background: transparent;
	cursor: pointer;

	:disabled {
		cursor: not-allowed;
	}
`;

const ScPrev = styled(ScNextandPrev)`
	margin-left: 8px;

	&:first-child {
		margin-left: 0;
	}
`;

const ScNext = styled(ScNextandPrev)`
	margin-right: 8px;

	&:last-child {
		margin-right: 0;
	}
`;

const ScPages = styled.span`
	margin-left: 16px;
	font-size: 14px;
`;

const ScGoTo = styled.span`
	border-left: 1px solid var(--font-body-color);
	padding-left: 8px;
	margin-left: 8px;
	margin-right: 16px;
	font-size: 14px;
	display: flex;
	align-items: center;
`;

const ScGoToInput = styled.input`
	width: 56px;
	margin-left: 4px;
	padding-left: 4px;
`;

const ScIcon = styled(Icon)`
	font-size: 15px;
`;

const ScButton = styled(Button)`
	margin-left: 8px;
	padding-left: 16px;
	padding-right: 16px;
`;

const ScSelectWrapper = styled.span`
	display: flex;
	align-items: center;
`;

const ScSelect = styled(Select)`
	margin: 0 8px;
`;
